import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {Outsourced} from "../Outsourced";
import {OutsourcedSound} from "../sound/OutsourcedSound";
import {gsap} from "gsap";
import {OutsourcedSymbol} from "../reels/symbols/OutsourcedSymbol";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {LoopableList} from "../utils/LoopableList";
import {OutsourcedCrashGameIdlePresentation} from "./OutsourcedCrashGameIdlePresentation";

export class OutsourcedNormalResultPresentation implements FlowStatePresentation {

    constructor() {
    }

    public start(flowStateController:FlowStateController) {
        const tl:gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                flowStateController.setSkipCallback();
            }
        });
        tl.add(() => flowStateController.setSkipCallback(() => tl.seek("End", false)));

        const wasGodModePurchase = Outsourced.model.isGodMode;

        const targetMultiplier = Outsourced.model.startMultiplier;
        const bruh = Outsourced.reelView.multipliers.some((mul) => parseInt(mul.text.slice(1), 10) != targetMultiplier);
        if (Outsourced.model.hasStartingMultipliers() && bruh) {
            for (let reel = 0; reel < 4; reel++)
                for (let row = 0; row < 4; row++)
                    tl.add(() => {Outsourced.reelView.setMultiplierTo(targetMultiplier, reel, row)}, '<0.05');
            tl.add(() => {Outsourced.reelView.updateWays(Outsourced.model.preXwaysWays(), false)});
        }

        const stopAnimation = wasGodModePurchase ? this.godModeStop() : this.normalStop();
        const jurPadding = Outsourced.gameFlowController.calculatePaddingDuration(stopAnimation.duration());
        tl.add(stopAnimation, `>${Math.max(jurPadding, 0)}`);
        tl.addLabel("End");

        tl.add(() => {
            flowStateController.finish()
        });
    }

    public static landingSound(symbol:OutsourcedSymbol, index:number = 0):gsap.Callback {
        let landingSound = "";
        switch(symbol.symbolName) {
            case "W":
                landingSound = "wildLand";
                break;
            case "XW":
                landingSound = "xWaysLand";
                break;
            case "XN":
                landingSound = "xNudgeLand";
                break;
            case "SC":
                landingSound = `scatter${index}`;
                break;
            case "MAX":
                landingSound = `maks${index }`;
                break;
        }
        return landingSound == "" ? () => {} : () => {Outsourced.soundPlayer.playSound(landingSound)};
    }

    private normalStop():gsap.core.Timeline {
        const isQuickStop = Outsourced.apiPlugin.settings.get(APISetting.FAST_SPIN);
        const tl = gsap.timeline();
        const stickyScatter = Outsourced.model.buyFeatureName == "BOOSTED_BET" &&
                              Outsourced.model.data.reels[1][0] == "SC" &&
                              !Outsourced.model.firstXBetSpin;
        const boughtFreespins = Outsourced.model.buyFeatureName == "FREESPIN_3_SCATTERS" ||
                                Outsourced.model.buyFeatureName == "FREESPIN_4_SCATTERS";


        let scatterCount = stickyScatter ? 1 : 0;
        let maxCount = 0;

        Outsourced.reelView.reelModels
            .forEach((reel, index) => {
                if (stickyScatter && index == 4)
                    return;

                const reelStopTl = gsap.timeline();
                const stopDelay = isQuickStop
                                  ? 0
                                  : boughtFreespins
                                  ? 0.05
                                  : (scatterCount > 1 && scatterCount < 4)
                                  ? index > 11 ? 1 : 0.25
                                  : 0.05;


                // stop reels
                const stopSymbol = Outsourced.model.data.reels[reel.reelID][index % 4];
                if (stopSymbol == "SC")
                    scatterCount++;
                if (stopSymbol == "MAX")
                    maxCount++;

                reelStopTl.add(reel.stop(stopSymbol));
                reelStopTl.add(Outsourced.reelView.stopConveyorBelt(Math.floor(index / 4), index % 4));

                reelStopTl.add(Outsourced.soundPlayer.playSoundBased(`reelLanding${index % 2 == 0 ? 1 : 2}`))

                const symbolIndex = stopSymbol == "MAX"
                                    ? Math.floor(index / 4) + 1
                                    : stopSymbol == "SC"
                                    ? scatterCount
                                    : index
                reelStopTl.add(OutsourcedNormalResultPresentation.landingSound(reel.getSymbols()[0], symbolIndex));
                if (stopSymbol == "XN" || stopSymbol == "W" || stopSymbol == "XW" ||
                    (stopSymbol == "SC" && scatterCount > 1) ||
                    (stopSymbol == "MAX" && maxCount == reel.reelID && maxCount > 1))
                    reelStopTl.add(reel.getSymbols().map((sym) => sym.getLandAnimation()))

                if (maxCount == reel.reelID + 1 && stopSymbol == "MAX" && maxCount > 1)
                    reelStopTl.add(Outsourced.reelView.playLandDust(Math.floor(index / 4), index % 4), '<');

                // anticipation stuff
                if (scatterCount > 1)
                    reelStopTl.add(() => {
                        Outsourced.reelView.getAllStoppedSymbols()
                            .filter((sym) => sym.symbolName == "SC")
                            .forEach((sym) => sym.startScatterAnticipation());
                    })
                if ((stopSymbol == "SC" && scatterCount > 1) || (stopSymbol == "MAX" && maxCount == reel.reelID + 1 && maxCount > 1))
                    reelStopTl.add(Outsourced.backgroundElements.shake(), '<');
                if (stopSymbol == "SC" && scatterCount == 2)
                    reelStopTl.add(Outsourced.soundPlayer.playLoopBased("anticipation"), '<');
                if ((stopSymbol == "SC" && scatterCount == 4) || index == 15)
                    reelStopTl.add(Outsourced.soundPlayer.stopLoopBased("anticipation"), '<');
                if (scatterCount > 1 && scatterCount != 4 && index < 12 && index > 7)
                    reelStopTl.add(Outsourced.soundPlayer.playSoundBased("anticipationReelStopColumn3"), '<');
                if (scatterCount > 1 && scatterCount != 4 && index > 11)
                    reelStopTl.add(Outsourced.soundPlayer.playSoundBased(`anticipationLastReel${index - 11}`), '<');
                if (index == 15 && scatterCount < 3 && scatterCount > 1)
                    reelStopTl.add(Outsourced.soundPlayer.playSoundBased("anticipationFail"), '<');
                if (stopSymbol == "SC" || stopSymbol == "MAX")
                    reelStopTl.add(() => {Outsourced.reelView.darkenMultiplier(Math.floor(index / 4), index % 4)});


                tl.add(reelStopTl, `<${stopDelay}`);
            });
        tl.add(Outsourced.soundPlayer.stopLoopBased(OutsourcedSound.REEL_SPINNING));
        tl.add(() => {
            Outsourced.reelView.getAllStoppedSymbols()
                               .filter((sym) => sym.symbolName == "SC")
                               .forEach((sym) => sym.stopScatterAnticipation());
        }, '<')
        tl.add(() => {
            // to fix gsap not firing lambdas (play and loop sound functions) in the right order during fast spins
            Outsourced.soundPlayer.stopLoopBased("anticipation")
            Outsourced.reelView.extractSymbols();
        });
        return tl;
    }

    private godModeStop():gsap.core.Timeline {
        const tl = gsap.timeline();
        const stickyScatter = Outsourced.model.buyFeatureName == "BOOSTED_BET" &&
            Outsourced.model.data.reels[1][0] == "SC" &&
            !Outsourced.model.firstXBetSpin;


        let scatterCount = 0;
        let maxCount = 0;

        Outsourced.reelView.reelModels
            .forEach((reel, index) => {
                if (stickyScatter && index == 4)
                    return;

                const reelStopTl = gsap.timeline();
                const stopDelay = (index > 11 && maxCount < 4) ? 0.5 : 0.05;


                // stop reels
                const stopSymbol = Outsourced.model.data.reels[reel.reelID][index % 4];
                if (stopSymbol == "SC")
                    scatterCount++;
                if (stopSymbol == "MAX")
                    maxCount++;

                reelStopTl.add(reel.stop(stopSymbol));
                reelStopTl.add(Outsourced.reelView.stopConveyorBelt(Math.floor(index / 4), index % 4));

                reelStopTl.add(Outsourced.soundPlayer.playSoundBased(`reelLanding${index % 2 == 0 ? 1 : 2}`))

                const symbolIndex = stopSymbol == "MAX"
                                    ? maxCount
                                    : stopSymbol == "SC"
                                    ? scatterCount
                                    : index
                reelStopTl.add(OutsourcedNormalResultPresentation.landingSound(reel.getSymbols()[0], symbolIndex));
                if (stopSymbol == "XN" || stopSymbol == "W" || stopSymbol == "XW" ||
                    (stopSymbol == "SC" && scatterCount > 1) ||
                    (stopSymbol == "MAX" && maxCount > 1))
                    reelStopTl.add(reel.getSymbols().map((sym) => sym.getLandAnimation()))

                if (index > 3 && stopSymbol == "MAX")
                    reelStopTl.add(Outsourced.reelView.playLandDust(Math.floor(index / 4), index % 4), '<');

                if (stopSymbol == "MAX" && maxCount > 1)
                    reelStopTl.add(Outsourced.backgroundElements.shake(), '<');

                if (index == 15 && maxCount < 4)
                    reelStopTl.add(Outsourced.soundPlayer.playSoundBased("anticipationFail"));
                if (index > 11 && stopSymbol == "MAX")
                    reelStopTl.add(Outsourced.backgroundElements.shake(), '<');
                if (index > 11 && maxCount < 4)
                    reelStopTl.add(Outsourced.soundPlayer.playSoundBased("godModeReelStop"), '<');

                tl.add(reelStopTl, `<${stopDelay}`);
            });
        tl.add(Outsourced.soundPlayer.stopLoopBased(OutsourcedSound.REEL_SPINNING));
        tl.add(() => {
            Outsourced.reelView.extractSymbols();
        }, '>0.1');
        return tl;
    }
}